import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import Select2Field from "components/Select2Field";
import Swal from "sweetalert2";
import { useTranslate } from "utils/i18n";
import { figmaTokens } from "assets/theme/designTokens";
import {
  buildTeacherPatch,
  fetchTeacherRecruiterOptions,
  teacherEditValues,
  teacherUpdateFieldErrors,
  updateTeacher,
} from "services/teacherUpdateService";
import { MAP_TEACHER_DEGREES } from "layouts/newmap/mapHelpers";

const fieldLabelSx = {
  display: "block",
  mb: 0.65,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

function FieldError({ message }) {
  return message ? <div className="error-message">{message}</div> : null;
}

FieldError.propTypes = { message: PropTypes.string };

function validate(values) {
  const errors = {};
  const email = String(values.email ?? "").trim();
  const recruiterId = String(values.recruiterId ?? "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Invalid email address";
  if (recruiterId && (!/^\d+$/.test(recruiterId) || Number(recruiterId) <= 0)) {
    errors.recruiterId = "Select a valid recruiter";
  }
  return errors;
}

export default function TeacherEditDialog({ open, teacherId, profile, onClose, onUpdated, onNotFound }) {
  const { t } = useTranslate();
  const [recruiters, setRecruiters] = useState([]);
  const [recruitersLoading, setRecruitersLoading] = useState(false);

  const formik = useFormik({
    initialValues: teacherEditValues(),
    validate,
    onSubmit: async (values, { setFieldError, setFieldTouched, setSubmitting }) => {
      const changes = buildTeacherPatch(formik.initialValues, values);
      if (!Object.keys(changes).length) {
        setSubmitting(false);
        return;
      }

      try {
        const response = await updateTeacher(teacherId, changes);
        if (response?.success === false) throw new Error(response?.message || "Teacher update failed");

        onClose();
        Swal.fire({
          icon: "success",
          title: t("teacher_edit.success", "Teacher updated successfully"),
          text: response?.message || t("teacher_edit.success_text", "Teacher updated successfully."),
          showConfirmButton: false,
          timer: 2200,
        });
        await onUpdated();
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) return;

        if (status === 409) {
          setFieldTouched("regNumber", true, false);
          setFieldError(
            "regNumber",
            t("teacher_edit.reg_conflict", "This matricule is already assigned to another teacher")
          );
          return;
        }

        if (status === 422) {
          const fieldErrors = teacherUpdateFieldErrors(error);
          Object.entries(fieldErrors).forEach(([field, message]) => {
            setFieldTouched(field, true, false);
            setFieldError(field, message);
          });
          if (Object.keys(fieldErrors).length) return;
        }

        if (status === 403) {
          await Swal.fire({
            icon: "error",
            title: t("teacher_edit.forbidden", "Only Super Admin can edit teacher details."),
            confirmButtonColor: figmaTokens.accent,
          });
          return;
        }

        if (status === 404) {
          await Swal.fire({
            icon: "error",
            title: t("teacher_edit.not_found", "Teacher was not found"),
            confirmButtonColor: figmaTokens.accent,
          });
          await onNotFound();
          return;
        }

        await Swal.fire({
          icon: "error",
          title: t("teacher_edit.error", "Unable to update the teacher. Please try again."),
          confirmButtonColor: figmaTokens.accent,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    formik.resetForm({ values: teacherEditValues(profile) });
    // Reset exactly when a new already-loaded profile is opened for editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setRecruitersLoading(true);
    fetchTeacherRecruiterOptions()
      .then((options) => {
        if (active) setRecruiters(options);
      })
      .catch(() => {
        if (active) setRecruiters([]);
      })
      .finally(() => {
        if (active) setRecruitersLoading(false);
      });
    return () => { active = false; };
  }, [open]);

  const recruiterOptions = useMemo(() => {
    const options = [{ id: "", text: t("teachers.recruiter_none", "No recruiter") }, ...recruiters];
    const currentId = String(formik.values.recruiterId ?? "").trim();
    if (currentId && !options.some((option) => String(option.id) === currentId)) {
      options.push({ id: currentId, text: profile?.recruiter ?? profile?.Recruiter ?? currentId });
    }
    return options;
  }, [recruiters, formik.values.recruiterId, profile, t]);

  const genderOptions = useMemo(() => {
    const options = [
      { id: "", text: t("teachers.gender_placeholder", "Select gender") },
      { id: "Homme", text: t("gender.male", "Male") },
      { id: "Femme", text: t("gender.female", "Female") },
    ];
    const current = String(formik.values.gender ?? "").trim();
    if (current && !options.some((option) => option.id === current)) options.push({ id: current, text: current });
    return options;
  }, [formik.values.gender, t]);

  const degreeOptions = useMemo(() => {
    const options = [
      { id: "", text: t("map.filter.all_degrees", "No degree selected") },
      ...MAP_TEACHER_DEGREES.map((id) => ({ id, text: id })),
    ];
    const current = String(formik.values.degree ?? "").trim();
    if (current && !options.some((option) => option.id === current)) options.push({ id: current, text: current });
    return options;
  }, [formik.values.degree, t]);

  const hasChanges = Object.keys(buildTeacherPatch(formik.initialValues, formik.values)).length > 0;
  const fieldError = (field) => (formik.touched[field] ? formik.errors[field] : undefined);
  const textField = (name, label, type = "text") => (
    <Grid item xs={12} sm={6} md={4}>
      <SoftTypography component="label" sx={fieldLabelSx}>{label}</SoftTypography>
      <SoftInput
        type={type}
        name={name}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        disabled={formik.isSubmitting}
      />
      <FieldError message={fieldError(name)} />
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={formik.isSubmitting ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
        <SoftBox sx={{ px: 3, py: 2.25, pr: 7, position: "relative" }}>
          <SoftTypography variant="h6" fontWeight="bold">
            {t("teacher_edit.title", "Edit Teacher")}
          </SoftTypography>
          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 0.5 }}>
            {t("teacher_edit.hint", "Update teacher details. The profile image remains unchanged.")}
          </SoftTypography>
          <IconButton
            aria-label={t("teachers.cancel", "Cancel")}
            onClick={onClose}
            disabled={formik.isSubmitting}
            sx={{ position: "absolute", right: 12, top: 14 }}
          >
            <CloseIcon />
          </IconButton>
        </SoftBox>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <form id="teacher-edit-form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {textField("regNumber", t("teachers.reg_number", "Matricule"))}
            {textField("name", t("teachers.name", "Last name"))}
            {textField("preName", t("teachers.firstname", "First name"))}

            <Grid item xs={12} sm={6} md={4}>
              <SoftTypography component="label" sx={fieldLabelSx}>{t("teachers.gender", "Gender")}</SoftTypography>
              <Select2Field
                name="gender"
                options={genderOptions}
                value={formik.values.gender}
                onChange={(value) => formik.setFieldValue("gender", value)}
                disabled={formik.isSubmitting}
              />
              <FieldError message={fieldError("gender")} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SoftTypography component="label" sx={fieldLabelSx}>{t("teachers.degree", "Degree")}</SoftTypography>
              <Select2Field
                name="degree"
                options={degreeOptions}
                value={formik.values.degree}
                onChange={(value) => formik.setFieldValue("degree", value)}
                disabled={formik.isSubmitting}
              />
              <FieldError message={fieldError("degree")} />
            </Grid>
            {textField("phone", t("teachers.phone", "Phone"), "tel")}
            {textField("whatsapp", t("teachers.table.whatsapp", "WhatsApp"), "tel")}
            {textField("email", t("teachers.table.email", "Email"), "email")}
            {textField("regDate", t("teachers.reg_date", "Registration date"), "date")}
            {textField("identificationNumber", t("teachers.identification", "Identification number"))}
            {textField("dob", t("teachers.dob", "Date of birth"), "date")}

            <Grid item xs={12} sm={6} md={4}>
              <SoftTypography component="label" sx={fieldLabelSx}>{t("teachers.recruiter", "Recruiter")}</SoftTypography>
              <Select2Field
                name="recruiterId"
                options={recruiterOptions}
                value={formik.values.recruiterId}
                onChange={(value) => formik.setFieldValue("recruiterId", value)}
                disabled={formik.isSubmitting || recruitersLoading}
              />
              <FieldError message={fieldError("recruiterId")} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SoftTypography component="label" sx={fieldLabelSx}>{t("teachers.person_type", "Person type")}</SoftTypography>
              <Select2Field
                name="isTeacher"
                options={[
                  { id: "true", text: t("teachers.type.teacher", "Teacher") },
                  { id: "false", text: t("teachers.type.staff", "Staff") },
                ]}
                value={String(formik.values.isTeacher)}
                onChange={(value) => formik.setFieldValue("isTeacher", value === "true")}
                disabled={formik.isSubmitting}
              />
              <FieldError message={fieldError("isTeacher")} />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: figmaTokens.pageBg, borderTop: `1px solid ${figmaTokens.cardHeaderRule}` }}>
        <SoftButton variant="outlined" color="secondary" onClick={onClose} disabled={formik.isSubmitting}>
          {t("teachers.cancel", "Cancel")}
        </SoftButton>
        <SoftButton
          type="submit"
          form="teacher-edit-form"
          variant="gradient"
          color="info"
          disabled={formik.isSubmitting || !hasChanges}
        >
          {formik.isSubmitting ? t("teachers.loading", "Saving...") : t("teachers.save", "Save")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

TeacherEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  teacherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  profile: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  onNotFound: PropTypes.func.isRequired,
};
